import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { friendlyAxiosError } from "@/lib/api";

const AuthContext = createContext(null);

const PREVIEW_KEY = "sos_preview_as_client";

export function AuthProvider({ children }) {
  const [realUser, setRealUser] = useState(null); // null = checking, false = not authed
  const [loading, setLoading] = useState(true);
  // Admin-only UX toggle. When on, any `user.role === "client"` check
  // across the app resolves to true so the admin sees the exact
  // ClientLayout + ClientDashboard + client vessel view. Backend
  // permissions are untouched — this is purely presentational.
  const [previewAsClient, setPreviewAsClient] = useState(() => {
    try {
      return localStorage.getItem(PREVIEW_KEY) === "1";
    } catch {
      return false;
    }
  });

  const setUser = setRealUser;

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setLoading(false);
      return;
    } catch {
      // fall through to auto-login
    }
    // Auto-login (preview / staging mode). Backend gates this with AUTO_LOGIN_ENABLED env flag.
    try {
      const { data } = await api.post("/auth/auto-login");
      if (data.access_token) localStorage.setItem("sos_access_token", data.access_token);
      setUser(data.user);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("sos_access_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: friendlyAxiosError(e) };
    }
  };

  const acceptInvite = async (token, name, password) => {
    try {
      const { data } = await api.post("/invites/accept", { token, name, password });
      if (data.access_token) localStorage.setItem("sos_access_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: friendlyAxiosError(e) };
    }
  };

  // Public client self-registration. Reuses the same JWT/cookie flow as
  // accept_invite — the only difference is the user's role on creation.
  const register = async (fields) => {
    try {
      const { data } = await api.post("/auth/register", fields);
      if (data.pending) return { ok: true, pending: true, message: data.message };
      if (data.access_token) localStorage.setItem("sos_access_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: friendlyAxiosError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* logout is best-effort */ }
    localStorage.removeItem("sos_access_token");
    // Never keep preview mode across a logout — could confuse the next
    // person who logs in on this browser.
    try { localStorage.removeItem(PREVIEW_KEY); } catch { /* localStorage may be blocked */ }
    setPreviewAsClient(false);
    setUser(false);
  };

  const togglePreviewAsClient = useCallback((next) => {
    setPreviewAsClient((cur) => {
      const value = typeof next === "boolean" ? next : !cur;
      try {
        if (value) localStorage.setItem(PREVIEW_KEY, "1");
        else localStorage.removeItem(PREVIEW_KEY);
      } catch { /* localStorage may be blocked */ }
      return value;
    });
  }, []);

  // Derive the "effective user" the rest of the app sees. When an admin
  // has toggled `previewAsClient`, we swap the role to `client` (and
  // stash the real role on `_real_role` for the diagnostic banner) so
  // every `user.role === "client"` check across the codebase resolves
  // to true — no per-page changes needed.
  const isAdminReal = realUser && realUser !== false && (realUser.role === "admin" || realUser.role === "staff");
  const user =
    previewAsClient && isAdminReal
      ? { ...realUser, role: "client", _real_role: realUser.role, _preview: true }
      : realUser;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        acceptInvite,
        register,
        refresh,
        previewAsClient: previewAsClient && isAdminReal,
        togglePreviewAsClient,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
